// @ts-strict-ignore
import { Subject, Subscription } from "rxjs";
import { CallRB } from "rainbow-web-sdk/models/call.model";
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { DEVICE_MANAGEMENT_SVC, DeviceManagementServiceRB } from "rainbow-web-sdk/services/deviceManagement/deviceManagement.service";
import { ContactService } from "rainbow-web-sdk/services/core/contact.service";
import { EventService } from "rainbow-web-sdk/services/core/event.service";
import { Service } from "rainbow-web-sdk/services/service";
import { ProfileService } from "../core";
import { MediaStreamKind } from "../../libs/strophe/strophe.jingle.session";
import { WebrtcSessionStatsHandler } from "./webrtcSessionStatsHandler";
import { stringifyError } from "../../utils/stringifyError";

export const WEBRTC_CONFIG_UTILS_SVC = 'WebrtcConfigAndUtils';

export interface IWebrtcConfigAndUtils {
    RTC: any;
}

// export interface BrowserMediaConstraints {
//     isMediaPillarCall?: boolean;
//     isConferenceCall?: boolean;
//     isWebinarCall?: boolean;
//     isWebCall?: boolean;
//     isWebCallWithMobile?: boolean;
//     customSharingFrameRate?: number;
//     getSystemAudio?: boolean;
// }

/**
 * This module contains all the webrtc configuration tools and utils that can be used to manages webrtc audio/video calls
 * This module can be used to set the video quality and frame rate for all P2P calls
 * The lifecycle of this module is managed by the WebRTC P2P Service
 */

export class WebrtcConfigAndUtils extends Service implements IWebrtcConfigAndUtils {
    /**
     * The current webrtc configuration used (TURN servers and DC)
     */
    public webrtcConfig: any;

    /**
     * The current webrtc company configuration (if set for the company)
     */
    public companyConfig: any;

    /**
     * The current webrtc video and sharing quality configuration when in call with mobile phone
     */
    public mobileVideoQualityConfiguration = {
        video: {
            fixedFrameRate: 20,
            fixedVideoWidth: 960,
            fixedVideoHeight: 540
        },
        sharing: {
            fixedFrameRate: 5,
            fixedVideoWidth: 1280,
            fixedVideoHeight: 720
        }
    };

    /**
     * The current webrtc video and sharing quality configuration
     */
    public videoQualityConfiguration = {
        video: {
            fixedFrameRate: 30,
            fixedVideoWidth: 1280,
            fixedVideoHeight: 720
        },
        sharing: {
            fixedFrameRate: 10,
            fixedVideoWidth: 1920,
            fixedVideoHeight: 1080
        }
    };

    /**
     * The RTC object that contains the attach and detach functions to add stream to DOM element based on the platform
     * The attachSessionMediaToElement is used for session that have only 1 media (for conferences case); We need to send the HTML element ID + the session that we want to "attach" to that element
     */
    public RTC: any = {
        browser: "",
        attachMediaStream: () => { },
        clearMediaStream: () => { },
        attachMediaStreamIfNeeded: () => { },
        attachSessionMediaToElement: () => { }
    };

    /**
     * Subscribe to updates on the current calls sessions (all events are of RBEvent type `{ name: string, data:any }`);
     * All public events that are fired by this service are described in the documentation.
     * NB There might be some events that are not documented here, they should be ignored
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     */
    public subscribe(handler: any): Subscription {
        return this.rxSubject.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch { /* empty */ }
        });
    }

    /**
     * Send an event to the RX Subject (sends always a RBEvent type)
     * @param string - name The name of the event
     * @param data - The data of the event
     */
    private sendEvent(name, data = null): void {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /**
     * The Calls Stats object that contains all the statistics for the current call (that will be uploaded to the server at the end of the call)
     */
    public callsStats = {};

    public askToAddSharing(__conversationId: string, __shouldAskForAudio: boolean = false) {
        return false;
    }

    private logger: LoggerService = LoggerService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB;
    private profileService: ProfileService = ProfileService.getInstance();
    private rxSubject: Subject<any> = new Subject<any>();
    private webrtcSessionStatsHandler;

    private jingleSubscription: Subscription = undefined;
    private localPeerConnection = {
        /**
         *  This 'inputPeerConnection' is the RTCPeerConnection instance used to manage the input stream of the call.
        */
        inputPeerConnection: undefined,

        /**
         *  This 'ouputPeerConnection' is the RTCPeerConnection instance used to manage the output stream of the call.
        */
        ouputPeerConnection: undefined
    };

    private ttl: number = 0;
    private ttlTimeout: any = null;

    private started: boolean = false;

    private statsWaitingQueue: Record<string, any>[] = [];

    public static getInstance() {
        return Service.create(WEBRTC_CONFIG_UTILS_SVC, WebrtcConfigAndUtils);
    }

    public static build() {
        return new WebrtcConfigAndUtils();
    }

    private constructor() {
        super();
        if (this.xmppService.connected) { this.start(); }
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); });
    }

    public async start() {
        this.deviceManagementService = this.mainService.inject(DEVICE_MANAGEMENT_SVC);
        this.webrtcSessionStatsHandler = new WebrtcSessionStatsHandler(this);
        this.logger.log('[WebrtcConfigAndUtils] -- start');
        if (!this.RTC?.browser) {
            this.RTC = this.setBrowserMethodHandler();
        }
        await this.setWebrtcConfiguration();
        this.started = true;

        if (this.statsWaitingQueue.length > 0) {
            for (const element of this.statsWaitingQueue) {
                await this.updateStatisticsForCall(element.id, element.callId, element.callState, element.metrics);
            }

            this.statsWaitingQueue = [];
        }

        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = null; }
        this.jingleSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((evt: RBEvent) => { this.onJingleMessage(evt); });

        this.makeLocalEchoTest();
    }

    public stop() {
        this.logger.log('[WebrtcConfigAndUtils] -- stop');
        this.ttl = 0;
        this.webrtcConfig = null;
        this.companyConfig = null;
        if (this.ttlTimeout) {
            clearTimeout(this.ttlTimeout);
            this.ttlTimeout = null;
        }
        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = null; }
    }

    private async onConnectionStateChangeEvent(event: RBEvent) {
        if (event.name === 'ON_XMPP_DISCONNECTED') {
            this.started = false;
            if (event.data.reason === "LOGOUT") {
                this.stop();
            }
            else {
                this.logger.log('[WebrtcConfigAndUtils] onConnectionStateChangeEvent : disconnected');
                if (this.ttlTimeout) {
                    clearTimeout(this.ttlTimeout);
                    this.ttlTimeout = null;
                }
                //when we reconnect, this will force the refresh of the ICE servers configuration
                this.ttl = 0;
            }

        }
        else if (event.name === 'ON_XMPP_CONNECTED') {
            this.logger.log('[WebrtcConfigAndUtils] onConnectionStateChangeEvent : connected');
            if (!this.started) {
                await this.start();
            }

            if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = null; }
            this.jingleSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((evt: RBEvent) => { this.onJingleMessage(evt); });
        }
    }

    private onJingleMessage(event: RBEvent): void {
        try {
            switch (event.name) {
                case 'jingleTurnCredentialExpire': this.jingleTurnCredentialExpire(event.data.sid); break;
                default:
                    break;
            }
        }
        catch (error) {
            this.logger.info(`[WebrtcConfigAndUtils] onJingleMessage -- failure -- ${event.name}`);
        }
    }

    private makeLocalEchoTest(): void {
        setTimeout(() => {
            if (this.localPeerConnection.inputPeerConnection) return;
            if (this.settingsService.getSetting("citrixConnected") === "true" || this.settingsService.getSetting("horizonConnected") === "true") return;
            this.buildLocalPeerConnection();
        }, Math.floor(Math.random() * 90) * 1000);
    }

    /**
     * This method builds the local peer connection
     */
    private async buildLocalPeerConnection() {
        let rtcDataChannel;
        try {
            this.logger.info(`[WebrtcConfigAndUtils] buildLocalPeerConnection`);
            const localConfig = Object.assign({}, this.webrtcConfig);
            localConfig.iceTransportPolicy = 'relay';
            this.localPeerConnection.inputPeerConnection = new RTCPeerConnection(localConfig);
            this.localPeerConnection.inputPeerConnection.onicecandidate = event => this.localPeerConnection.ouputPeerConnection.addIceCandidate(event.candidate);

            //  ---------------------------------------------------------------------------------------
            //  Create output RTC peer connection
            //  ---------------------------------------------------------------------------------------
            this.localPeerConnection.ouputPeerConnection = new RTCPeerConnection(localConfig);
            this.localPeerConnection.ouputPeerConnection.onicecandidate = event => this.localPeerConnection.inputPeerConnection.addIceCandidate(event.candidate);

            rtcDataChannel = this.localPeerConnection.inputPeerConnection.createDataChannel("localTest");

            rtcDataChannel.onopen = () => { this.logger.info(`[WebrtcConfigAndUtils] buildLocalPeerConnection -- dataChannel open`); };
            rtcDataChannel.onclose = () => { this.logger.info(`[WebrtcConfigAndUtils] buildLocalPeerConnection -- dataChannel onclose`); rtcDataChannel.onopen = undefined; rtcDataChannel.onclose = undefined; rtcDataChannel.onerror = undefined; rtcDataChannel = undefined; };
            rtcDataChannel.onerror = () => { this.logger.info(`[WebrtcConfigAndUtils] buildLocalPeerConnection -- dataChannel onerror`); };
            //  ---------------------------------------------------------------------------------------
            //  Make RTC call
            //  ---------------------------------------------------------------------------------------

            const offer = await this.localPeerConnection.inputPeerConnection.createOffer({ iceRestart: true });
            await this.localPeerConnection.inputPeerConnection.setLocalDescription(offer);
            await this.localPeerConnection.ouputPeerConnection.setRemoteDescription(offer);

            const answer = await this.localPeerConnection.ouputPeerConnection.createAnswer();
            await this.localPeerConnection.ouputPeerConnection.setLocalDescription(answer);
            await this.localPeerConnection.inputPeerConnection.setRemoteDescription(answer);

            setTimeout(() => {
                this.destroyLocalPeerConnection();
                if (rtcDataChannel) {
                    rtcDataChannel.close();
                }
            }, 15000);
        }
        catch (error) {
            this.logger.warn(`[WebrtcConfigAndUtils] buildLocalPeerConnection -- error ${error} ${error?.message}`);
            this.destroyLocalPeerConnection();
            if (rtcDataChannel) {
                rtcDataChannel.close();
            }
        }
    }

    private destroyLocalPeerConnection() {
        this.logger.info(`[WebrtcConfigAndUtils] destroyLocalPeerConnection`);
        if (this.localPeerConnection.inputPeerConnection) { this.localPeerConnection.inputPeerConnection.close(); this.localPeerConnection.inputPeerConnection = undefined; }
        if (this.localPeerConnection.ouputPeerConnection) { this.localPeerConnection.ouputPeerConnection.close(); this.localPeerConnection.ouputPeerConnection = undefined; }
    }

    public getBrowserMethodHandler() {
        return this.RTC;
    }

    private async jingleTurnCredentialExpire(sessionId: string) {
        this.logger.log(`[WebrtcConfigAndUtils] jingleTurnCredentialExpire ${sessionId} -- reset configuration`);
        this.webrtcConfig = undefined;
        try { await this.setIceConfig(); }
        catch { this.logger.info("[WebrtcConfigAndUtils] setIceConfig -- error -- proceed anyway"); }

        //reconnect the session
        const session = this.xmppService.connection.jingle?.sessions[sessionId];
        if (session) {
            //reset the ice config and do ice restart
            session.updateIceConfigAndReconnect(this.webrtcConfig.iceServers);
        }
    }

    //     private globalAudioTagObserver() {
    //         const audioCtx = new AudioContext();
    // const source = audioCtx.createMediaElementSource(audioElement);
    // const analyser = audioCtx.createAnalyser();
    // analyser.fftSize = 256;

    // source.connect(analyser);
    // analyser.connect(audioCtx.destination);

    // const dataArray = new Uint8Array(analyser.frequencyBinCount);

    // function checkIfSilent() {
    //   analyser.getByteTimeDomainData(dataArray);
    //   let total = 0;
    //   for (let i = 0; i < dataArray.length; i++) {
    //     const val = dataArray[i] - 128;
    //     total += val * val;
    //   }

    //   const rms = Math.sqrt(total / dataArray.length);
    //   const isSilent = rms < 2; // empirically chosen threshold
    //   console.log("Audio RMS:", rms, "Silent?", isSilent);
    // }

    // setInterval(checkIfSilent, 200);

    //     }

    public setBrowserMethodHandler() {

        if (!window.adapter || window.adapter.browserDetails.browser === "chrome") {
            //chrome or desktop app case
            this.RTC = {
                browser: "chrome",
                attachMediaStream: (element, stream) => {
                    if (element?.[0]) {
                        this.logger.log(`[WebrtcConfigAndUtils] | attachMediaStream to element ${element[0].id} with sinkId ${element[0].sinkId}`);

                        element[0].srcObject = stream;
                        if (element[0].nodeName === "VIDEO") {
                            element[0].autoplay = true;
                            element[0].muted = true;
                        }
                        element[0].play();
                    }
                },
                clearMediaStream: (element) => {
                    if (element?.[0]) {
                        this.logger.log(`[WebrtcConfigAndUtils] | clearMediaStream for element  ${element[0]?.id}`);
                        element[0].pause();
                        element[0].srcObject = null;
                        element[0].src = '';
                        element[0].uniqueId = null;
                        element[0].load();
                    }
                },
                attachMediaStreamIfNeeded: (element, stream, uniqueId) => {
                    this.logger.log("[WebrtcConfigAndUtils] | attachMediaStreamIfNeeded for ID " + uniqueId);

                    if (element?.[0] && (element[0].uniqueId !== uniqueId || !element[0].srcObject)) {
                        element[0].uniqueId = uniqueId;
                        this.RTC.attachMediaStream(element, stream);
                    }
                    else if (element?.[0]?.srcObject && stream && element[0].srcObject.id !== stream.id) {
                        this.RTC.attachMediaStream(element, stream);
                        element[0].play();
                    }
                },
                attachSessionMediaToElement: (elementId: string, session: any) => {
                    const element = document.getElementById(elementId);
                    if (!element || !session) return;

                    //audio session unified plan
                    if (session.remoteStreamsObject["0"]) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.remoteStreamsObject["0"], session.sid);
                    }
                    else if (session.localStreams) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.localStreams[0], session.sid);
                    }
                }
            };
        }
        else if (window.adapter.browserDetails.browser === "firefox") {
            //firefox case
            this.RTC = {
                browser: "firefox",
                attachMediaStream: (element, stream) => {
                    if (element?.[0]) {
                        this.logger.log(`[WebrtcConfigAndUtils] | attachMediaStream to element ${element[0].id}`);
                        element[0].mozSrcObject = stream;
                        element[0].srcObject = stream;
                        element[0].play();
                    }
                },
                clearMediaStream: (element) => {
                    try {
                        if (element[0]) {
                            this.logger.log(`[WebrtcConfigAndUtils] | clearMediaStream for element  ${element[0]?.id}`);
                            element[0].pause();
                            element[0].mozSrcObject = null;
                            element[0].srcObject = null;
                            element[0].src = '';
                            element[0].uniqueId = null;
                            element[0].load();
                        }
                    } catch (err) {
                        this.logger.log("[WebrtcConfigAndUtils] | clearMediaStream failure " + err);
                    }
                },
                attachMediaStreamIfNeeded: (element, stream, uniqueId) => {
                    this.logger.log("[WebrtcConfigAndUtils] | attachMediaStreamIfNeeded for ID " + uniqueId);

                    if (element?.[0] && (element[0].uniqueId !== uniqueId || (!element[0].mozSrcObject && !element[0].srcObject))) {
                        element[0].uniqueId = uniqueId;
                        this.RTC.attachMediaStream(element, stream);
                    }
                    else if (element?.[0]?.srcObject && stream && element[0].srcObject.id !== stream.id) {
                        this.RTC.attachMediaStream(element, stream);
                        element[0].play();
                    }
                },
                attachSessionMediaToElement: (elementId: string, session: any) => {
                    const element = document.getElementById(elementId);
                    if (!element || !session) return;

                    //audio session unified plan
                    if (session.remoteStreamsObject["0"]) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.remoteStreamsObject["0"], session.sid);
                    }
                    else if (session.localStreams) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.localStreams[0], session.sid);
                    }
                }
            };
        }
        else {
            //safari case
            this.RTC = {
                browser: "safari",
                attachMediaStream: (element, stream) => {
                    if (element?.[0]) {
                        this.logger.log(`[WebrtcConfigAndUtils] | attachMediaStream to element ${element[0].id}`);
                        element[0].setAttribute("playsinline", true);
                        element[0].srcObject = stream;
                        element[0].play();
                    }
                },
                clearMediaStream: (element) => {
                    try {
                        if (element?.[0]) {
                            this.logger.log(`[WebrtcConfigAndUtils] | clearMediaStream for element  ${element[0]?.id}`);
                            element[0].pause();
                            element[0].srcObject = null;
                            element[0].src = '';
                            element[0].uniqueId = null;
                            element[0].load();
                        }
                    } catch (err) {
                        this.logger.log("[WebrtcConfigAndUtils] | clearMediaStream failure " + err);
                    }
                },
                attachMediaStreamIfNeeded: (element, stream, uniqueId) => {
                    this.logger.log("[WebrtcConfigAndUtils] | attachMediaStreamIfNeeded for ID " + uniqueId);

                    if (element?.[0] && (element[0].uniqueId !== uniqueId || !element[0].srcObject)) {
                        element[0].uniqueId = uniqueId;
                        this.RTC.attachMediaStream(element, stream);
                    }
                    else if (element?.[0]?.srcObject && stream && element[0].srcObject.id !== stream.id) {
                        this.RTC.attachMediaStream(element, stream);
                    }
                },
                attachSessionMediaToElement: (elementId: string, session: any) => {
                    const element = document.getElementById(elementId);
                    if (!element || !session) return;

                    //audio session unified plan
                    if (session.remoteStreamsObject["0"]) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.remoteStreamsObject["0"], session.sid);
                    }
                    else if (session.localStreams) {
                        this.RTC.attachMediaStreamIfNeeded([element], session.localStreams[0], session.sid);
                    }
                }
            };
        }

        return this.RTC;
    }

    public getDesktopSharingSource() {
        //do nothing
        return null;
    }

    private getConstraintsForMedia(userMedia: string[], fixedVideoWidth: number, fixedVideoHeight: number, fixedFrameRate: number, getAudioForSharing: boolean, isMediaPillarCall: boolean): any {

        let constraints: any = {};

        let width = fixedVideoWidth ? fixedVideoWidth : this.videoQualityConfiguration.video.fixedVideoWidth;
        let height = fixedVideoHeight ? fixedVideoHeight : this.videoQualityConfiguration.video.fixedVideoHeight;
        let frameRate = fixedFrameRate ? fixedFrameRate : this.videoQualityConfiguration.video.fixedFrameRate;

        if (userMedia.indexOf("sharing") >= 0) {
            width = fixedVideoWidth ? fixedVideoWidth : this.videoQualityConfiguration.sharing.fixedVideoWidth;
            height = fixedVideoHeight ? fixedVideoHeight : this.videoQualityConfiguration.sharing.fixedVideoHeight;
            frameRate = fixedFrameRate ? fixedFrameRate : this.videoQualityConfiguration.sharing.fixedFrameRate;
        }

        if (userMedia.indexOf("audio") >= 0) {
            const microphone = this.deviceManagementService.getCurrentMicrophone();
            let mic = "default";
            if (microphone?.deviceId) {
                constraints.audio = {
                    deviceId: { exact: microphone.deviceId }
                };
                mic = "id - " + microphone.deviceId + " and label " + microphone.label;

                //check if PBX MP call
                let disableNoiseSuppression: boolean = false;
                let disableEchoCancellation: boolean = false;
                if (isMediaPillarCall) {
                    //disable echo cancellation, but keep noiseSuppression
                    disableNoiseSuppression = false;
                    disableEchoCancellation = false;

                    //get values from company config, if any
                    if (this.companyConfig?.audioExt?.pbxEchoCancellation !== undefined) { disableEchoCancellation = !this.companyConfig?.audioExt?.pbxEchoCancellation; }
                    if (this.companyConfig?.audioExt?.pbxNoiseSuppression !== undefined) { disableNoiseSuppression = !this.companyConfig?.audioExt?.pbxNoiseSuppression; }

                    //overwrite with local settings
                    if (this.settingsService.getSetting("pbxNoiseSuppression")) {
                        disableNoiseSuppression = this.settingsService.getSetting("pbxNoiseSuppression") === "false";
                        this.logger.info(`[WebrtcConfigAndUtils] custom user settings -- pbxNoiseSuppression will be disabled: ${disableNoiseSuppression}`);
                    }
                    if (this.settingsService.getSetting("pbxEchoCancellation")) {
                        disableEchoCancellation = this.settingsService.getSetting("pbxEchoCancellation") === "false";
                        this.logger.info(`[WebrtcConfigAndUtils] custom user settings -- pbxEchoCancellation will be disabled: ${disableEchoCancellation}`);
                    }
                }

                //check if value from the local storage settings to overwrite the config
                if (this.settingsService.getSetting("noiseSuppression")) {
                    disableNoiseSuppression = this.settingsService.getSetting("noiseSuppression") === "false";
                    this.logger.info(`[WebrtcConfigAndUtils] custom user settings -- noiseSuppression will be disabled: ${disableNoiseSuppression}`);
                }
                if (this.settingsService.getSetting("echoCancellation")) {
                    disableEchoCancellation = this.settingsService.getSetting("echoCancellation") === "false";
                    this.logger.info(`[WebrtcConfigAndUtils] custom user settings -- echoCancellation will be disabled: ${disableEchoCancellation}`);
                }

                //if we disable one of the other settings, then add all of them in the asking
                if (disableEchoCancellation || disableNoiseSuppression) {
                    constraints.audio["noiseSuppression"] = !disableNoiseSuppression;
                    constraints.audio["echoCancellation"] = !disableEchoCancellation;
                }

                //add settings to disable audio media stream settings
                if (this.settingsService.getSetting("autoGainControlFlag") === "false") { constraints.audio["autoGainControl"] = false; }
                else { constraints.audio["autoGainControl"] = true; }
            }
            else {
                constraints.audio = true;
            }

            this.logger.info("[WebrtcConfigAndUtils] getBrowserMedia for microphone " + mic);
        }

        if (userMedia.indexOf("video") >= 0) {
            constraints.video = {
                width: width,
                height: height,
                frameRate: frameRate,
                facingMode: "user",
            };
            const camera = this.deviceManagementService.getCurrentCamera();
            let cam = "default";

            if (camera?.deviceId && camera.deviceId !== "default") {
                constraints.video.deviceId = { exact: camera.deviceId };
                cam = "id - " + camera.deviceId + " and label " + camera.label;
            }

            this.logger.info("[WebrtcConfigAndUtils] getBrowserMedia for camera " + cam);
        }

        //sharing constraints
        if (userMedia.indexOf("sharing") >= 0) {
            if (!this.mainService.isDesktopApp() && window.adapter.browserDetails.browser === "chrome") {
                if (navigator?.platform?.toUpperCase()?.indexOf('MAC') !== -1) {
                    //mac specific settings
                    constraints = {
                        audio: getAudioForSharing,
                        video: {
                            //add new property for Chrome 107+
                            displaySurface: "monitor",
                            chromeMediaSource: "desktop",
                            width: { ideal: width },
                            height: { ideal: height },
                            frameRate: { ideal: frameRate }
                        }
                    };
                }
                else {
                    constraints = {
                        audio: getAudioForSharing,
                        video: {
                            //add new property for Chrome 107+
                            displaySurface: "monitor",
                            chromeMediaSource: "desktop",
                            width: { max: width },
                            height: { max: height },
                            frameRate: { ideal: frameRate },
                            cursor: 'motion'
                        }
                    };
                }
            }
            //Firefox
            else if (window.adapter.browserDetails.browser === "firefox") {
                constraints.video = {
                    mediaSource: "window",
                    width: { ideal: width },
                    height: { ideal: height },
                    frameRate: { ideal: frameRate }
                };
            }
            //Otherwise
            else {
                //desktop app
                const sharingSources = this.getDesktopSharingSource();
                const chromeMediaSource = (sharingSources?.chromeMediaSource) ? sharingSources.chromeMediaSource : "desktop";
                const chromeMediaSourceId = (sharingSources?.chromeMediaSourceId) ? sharingSources.chromeMediaSourceId : null;

                if (navigator.platform?.toUpperCase()?.indexOf('MAC') !== -1) {
                    constraints.video = {
                        mandatory: {
                            chromeMediaSource: chromeMediaSource,
                            width: { ideal: width },
                            height: { ideal: height },
                            frameRate: { ideal: frameRate }
                        }
                    };
                }
                else {
                    constraints.video = {
                        mandatory: {
                            chromeMediaSource: chromeMediaSource,
                            maxWidth: width,
                            maxHeight: height,
                            maxFrameRate: frameRate
                        }
                    };
                }

                if (getAudioForSharing) {
                    constraints.audio = {
                        mandatory: {
                            chromeMediaSource: 'desktop'
                        }
                    };
                }

                if (chromeMediaSourceId) {
                    constraints.video.mandatory.chromeMediaSourceId = chromeMediaSourceId;
                }
            }
        }

        if (!constraints.audio) { constraints.audio = false; }
        if (!constraints.video) { constraints.video = false; }

        return constraints;
    }

    // public async askBrowserMedia(userMedia: string[], constraints?: BrowserMediaConstraints) {
    //     let fixedVideoWidth = 0, fixedVideoHeight = 0, fixedFrameRate = 0;
    //     let settings;

    //     if (constraints?.isWebCallWithMobile) {
    //         settings = this.getMobileMediaConstraints((userMedia.indexOf("sharing") !== -1) ? "sharing" : "video");
    //         fixedVideoWidth = settings.fixedVideoWidth;
    //         fixedVideoHeight = settings.fixedVideoHeight;
    //         fixedFrameRate = settings.fixedFrameRate;
    //     }

    //     if (constraints?.customSharingFrameRate > 0) {
    //         fixedFrameRate = constraints?.customSharingFrameRate
    //     }

    //     if (constraints?.isMediaPillarCall) {

    //     }

    //     return this.getBrowserMedia(userMedia, fixedVideoWidth, fixedVideoHeight, fixedFrameRate, null, constraints.getSystemAudio, constraints?.isMediaPillarCall);
    // }

    //TODO: move the getConstraints outside the API, and to be called before the getBrowserMedia is done; And send the ID of the call/conf/webinar
    //so that the noise reduction can be related to the call;
    public async getBrowserMedia(userMedia: any[], fixedVideoWidth: number = 0, fixedVideoHeight: number = 0, fixedFrameRate: number = 0, referer: any = null, getAudioForSharing: boolean = false, isMediaPillarCall: boolean = false): Promise<MediaStream | undefined> {
        try {

            if (!this.deviceManagementService.started) {
                await this.deviceManagementService.checkDeviceList();
                this.deviceManagementService.started = true;
            }

            let stream: MediaStream;

            if (!userMedia || userMedia.length === 0) return undefined;

            let shouldGetDisplayMedia = false;
            const constraints = this.getConstraintsForMedia(userMedia, fixedVideoWidth, fixedVideoHeight, fixedFrameRate, getAudioForSharing, isMediaPillarCall);

            if (userMedia.indexOf("sharing") >= 0 && !this.mainService.isDesktopApp() && window.adapter.browserDetails.browser === "chrome") {
                shouldGetDisplayMedia = true;
            }

            this.logger.info("[WebrtcConfigAndUtils] getBrowserMedia with constraint " + JSON.stringify(constraints));

            if (!constraints) {
                throw new Error("getBrowserMedia cancelled");
            }

            if (shouldGetDisplayMedia) {
                stream = await (navigator.mediaDevices as any).getDisplayMedia(constraints);
                this.logger.info("[WebrtcConfigAndUtils] | getBrowserMedia (getDisplayMedia) success");
            }
            else {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
                this.logger.info("[WebrtcConfigAndUtils] | getBrowserMedia (getUserMedia) success");
            }

            //add logs
            if (stream.getAudioTracks().length) {
                this.logger.info(`[WebrtcConfigAndUtils] getBrowserMedia audio stream kind ${stream.getAudioTracks()[0].kind}, id ${stream.getAudioTracks()[0].id}, label ${stream.getAudioTracks()[0].label}, readyState ${stream.getAudioTracks()[0].readyState}, enabled ${stream.getAudioTracks()[0].enabled} and settings ${JSON.stringify(stream.getAudioTracks()[0].getSettings())}`);

                stream.getAudioTracks()[0].onended = () => {
                    this.logger.error("[WebrtcConfigAndUtils] getUserMedia -- audio stream track ended");
                    this.eventService.publish("ON_AUDIO_STREAM_TRACK_ENDED");
                };
            }

            if (stream.getVideoTracks().length) {
                this.logger.info(`[WebrtcConfigAndUtils] getBrowserMedia video stream kind ${stream.getVideoTracks()[0].kind}, id ${stream.getVideoTracks()[0].id}, label ${stream.getVideoTracks()[0].label}, readyState ${stream.getVideoTracks()[0].readyState}, enabled ${stream.getVideoTracks()[0].enabled} and settings ${JSON.stringify(stream.getVideoTracks()[0].getSettings())}`);
            }

            if (stream && ((stream.getAudioTracks().length === 0 && stream.getVideoTracks().length === 0) || !stream.active)) {
                this.logger.warn("[WebrtcConfigAndUtils] getBrowserMedia Stream has no active audio or video tracks");
                // GA track GetUserMedia error
                throw new Error("getDisplayMedia failure : getBrowserMedia Stream has no active audio or video tracks");
            }

            //Add eventHandler for chrome stop sharing button
            if (userMedia.indexOf("sharing") >= 0) {
                stream["sharingStream"] = true;

                stream.getVideoTracks()[0].onended = () => {
                    this.logger.info("Stop screensharing native button has been triggered");
                    this.eventService.publish("ON_CHROME_STOP_SCREEN_SHARING_TRIGGERED");
                    this.sendEvent("ON_CHROME_STOP_SCREEN_SHARING_TRIGGERED", { webinar: referer });
                };

                //activate content hint for sharing streams
                stream.getVideoTracks()[0].contentHint = "detail";
                if (stream.getAudioTracks()?.length) {
                    stream["sharingAndAudio"] = true;

                    //we need to update the FPS to the sharing, as there's audio with it
                    try {
                        const frameRate = 24;
                        const track = stream.getVideoTracks()[0];
                        // Apply frame rate constraints on src track
                        const constraints = track.getConstraints();
                        if (frameRate) constraints.frameRate = frameRate;
                        await track.applyConstraints(constraints);
                    }
                    catch (error) { this.logger.error(`[WebrtcConfigAndUtils] applyConstraintsToSharingStream -- failure -- ${stringifyError(error)}`); }
                }

            }
            else if (userMedia.indexOf("video") >= 0) {
                //activate content hint for video streams
                stream.getVideoTracks()[0].contentHint = "motion";
            }

            //calculate the stream kind
            stream["mediaStreamKind"] = MediaStreamKind.AUDIO;

            if (userMedia.indexOf("sharing") !== -1) {
                stream["mediaStreamKind"] = stream.getAudioTracks()?.length ? MediaStreamKind.SHARING_BUNDLE : MediaStreamKind.SHARING;
            }
            else if (userMedia.indexOf("video") >= 0) {
                stream["mediaStreamKind"] = stream.getAudioTracks()?.length ? MediaStreamKind.AUDIO_VIDEO_BUNDLE : MediaStreamKind.VIDEO;
            }

            return stream;
        }
        catch (err) {
            this.logger.info("[WebrtcConfigAndUtils] | getBrowserMedia failure: " + err);
            throw new Error("getBrowserMedia failure " + err);
        }
    }

    /**
     * Disables and removes all local streams for this session
     * @param session - The session (or any object that contains localStreams that will be removed)
     */
    public disableAllStreamsForSession(session: any) {
        if (!session?.localStreams) {
            this.logger.log("[WebrtcConfigAndUtils] | disableAllStreamsForSession -- missing session or no localStreams");
            return;
        }

        if (session.localStreams.length > 0) {
            //We stop the streams of this session
            session.localStreams.forEach((stream: MediaStream) => {
                stream.getTracks().forEach((track: MediaStreamTrack) => {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                stream = null;
            });

            //remove session's streams
            while (session.localStreams.length > 0) {
                session.localStreams.pop();
            }
            this.logger.log("[WebrtcConfigAndUtils] | disableAllStreamsForSession : Session streams stopped and removed.");
        }
    }

    /**
     * Disables MediaStream
     * @param streams - An array of MediaStream to be stopped and removed
     */
    public disableMediaStreams(streams: MediaStream[]): void {
        if (streams?.length) {
            streams.forEach((stream: MediaStream) => {
                stream.getTracks().forEach((track: MediaStreamTrack) => {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                stream = null;
            });

            //remove session's streams
            while (streams.length > 0) {
                streams.pop();
            }
        }
    }

    /**
     * Disables MediaStream
     * @param stream - The MediaStream to be stopped and removed
     */
    public disableMediaStream(stream: MediaStream): void {
        if (stream) {
            //We stop the tracs of this stream
            stream.getTracks().forEach((track: MediaStreamTrack) => {
                track.enabled = false;
                track.stop();
                track = null;
            });

            stream = null;
        }
    }

    public getMobileMediaConstraints(type: string) {
        return this.mobileVideoQualityConfiguration[type];
    }

    public async createOrUpdateStatisticsForCall(call: CallRB) {
        try {
            if (!call) {
                throw new Error(`[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall -- missing call`);
            }

            if (!call.contact) {
                throw new Error(`[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall -- missing contact for call`);
            }

            const callId = call.id;
            const contactDbId = (call.contact.dbId) ? call.contact.dbId : call.contact.id;

            if (!callId || !contactDbId) {
                throw new Error(`[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall -- missing informations for ${callId} `);
            }

            const metrics = [];

            //check if statistics already exist
            if (this.callsStats[callId]) {
                if (this.callsStats[callId].RainMetrics) { this.callsStats[callId].push(this.callsStats[callId].RainMetrics); }
                metrics.push({ connectionId: callId, stats: this.callsStats[callId] });

                if (this.started) {
                    await this.updateStatisticsForCall(this.callsStats[callId].id, callId, "ended", metrics);
                } else {
                    this.statsWaitingQueue.push({ id: this.callsStats[callId].id, callId: callId, callState: "ended", metrics: metrics });
                }
                return;
            }

            this.logger.info("[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall " + callId);
            this.callsStats[callId] = {};
            metrics.push({ connectionId: callId, stats: this.callsStats[callId] });

            const id = await this.createStatisticsForSession(callId, contactDbId, "pending");
            this.callsStats[callId].id = id;
            this.logger.info("[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall success for session " + callId + " with id " + id);
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] createOrUpdateStatisticsForCall error -- " + error.message);
        }
    }

    public async updateStatisticsForCall(id: string, callId: string, callState: string, metrics: any): Promise<void> {
        try {
            if (!id) {
                throw new Error(`[WebrtcConfigAndUtils] updateStatisticsForCall -- missing id ${id}`);
            }

            this.logger.info("[WebrtcConfigAndUtils] updateStatisticsForCall " + callId);
            const portalURL = config.restServerUrl + "/api/rainbow/metrics/v1.0/";

            const response = await fetch(portalURL + "webrtcmetrics/" + id + "/metrics", {
                method: 'POST',
                headers: this.authService.getPostHeader(),
                body: JSON.stringify({
                    metrics: metrics
                })
            });
            if (!response.ok) throw (response);

            this.logger.info("[WebrtcConfigAndUtils] updateStatisticsForCall success");

            if (callState === "ended") {
                this.logger.info("[WebrtcConfigAndUtils] updateStatisticsForCall -- update call state with " + callState);
                const response2 = await fetch(portalURL + "webrtcmetrics/" + id, {
                    method: 'PUT',
                    headers: this.authService.getPostHeader(),
                    body: JSON.stringify({
                        callState: callState
                    })
                });
                if (!response2.ok) throw (response2);
                this.logger.info("[WebrtcConfigAndUtils] updateStatisticsForCall -- update call state success");
            }
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] updateStatisticsForCall failure " + error.errorMsg);

            if (!this.started) {
                this.logger.log("No internet connection");
            }
        }
    }

    public async createStatisticsForSession(callId: string, peerId: string, callState: string): Promise<string> {
        try {
            this.logger.info("[WebrtcConfigAndUtils] createStatisticsForSession " + callId);
            const portalURL = config.restServerUrl + "/api/rainbow/metrics/v1.0/webrtcmetrics";

            const response = await fetch(portalURL, {
                method: 'POST',
                headers: this.authService.getPostHeader(),
                body: JSON.stringify({
                    callId: callId,
                    peerId: peerId,
                    callState: callState
                })
            });
            if (!response.ok) throw (response);
            const result = (await response.json()).data;
            const id = result ? result.id : null;

            this.logger.info("[WebrtcConfigAndUtils] createStatisticsForSession success");
            if (id && !this.callsStats[callId]) {
                this.callsStats[callId] = {};
            }

            return id;
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] createStatisticsForSession failure " + error.errorMsg);
            throw error;
        }
    }

    public async setWebrtcConfiguration() {
        try { await this.setCompanyWebrtcConfig(); }
        catch { this.logger.info("[WebrtcConfigAndUtils] setCompanyWebrtcConfig -- error -- proceed anyway"); }

        try { await this.setIceConfig(); }
        catch { this.logger.info("[WebrtcConfigAndUtils] setIceConfig -- error -- proceed anyway"); }

        this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration -- done");

    }

    /**
     * Get the Media stream from the browser w/o any constraints (except for the selected current device);
     * @param userMedia - An array of medias to get (accepted values inside the array are : "audio" and "video");
     */
    public async getBrowserMediaWithNoConstraints(userMedia: string[]): Promise<MediaStream> {
        try {
            this.logger.info("[WebrtcConfigAndUtils] getBrowserMediaWithNoConstraints");
            const constraints = this.getDefaultConstraintsForMedia(userMedia);
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            this.logger.info("[WebrtcConfigAndUtils] getBrowserMediaWithNoConstraints -- done");
            if (stream.getAudioTracks().length) {
                this.logger.info("[WebrtcConfigAndUtils] getBrowserMediaWithNoConstraints audio stream kind " + stream.getAudioTracks()[0].kind + ", id " + stream.getAudioTracks()[0].id
                    + ", label " + stream.getAudioTracks()[0].label + ", readyState " + stream.getAudioTracks()[0].readyState + ", enabled " + stream.getAudioTracks()[0].enabled);
            }

            if (stream.getVideoTracks().length) {
                this.logger.info("[WebrtcConfigAndUtils] getBrowserMediaWithNoConstraints video stream kind " + stream.getVideoTracks()[0].kind + ", id " + stream.getVideoTracks()[0].id
                    + ", label " + stream.getVideoTracks()[0].label + ", readyState " + stream.getVideoTracks()[0].readyState + ", enabled " + stream.getVideoTracks()[0].enabled);
            }

            return stream;
        }
        catch (error) {
            this.logger.info(`[WebrtcConfigAndUtils] getBrowserMediaWithNoConstraints -- error -- ${stringifyError(error)}`);
            throw error;
        }
    }

    private getDefaultConstraintsForMedia(userMedia: string[]) {
        const constraints: any = {};

        this.logger.info(`[WebrtcConfigAndUtils] getDefaultConstraintsForMedia with medias: ${JSON.stringify(userMedia)}`);
        if (userMedia.indexOf("audio") >= 0) {
            const microphone = this.deviceManagementService.getCurrentMicrophone();
            constraints.audio = true;
            let mic = "default";
            // In case of Firefox, we should set another configuration
            if (microphone?.deviceId) {
                constraints.audio = {
                    deviceId: { exact: microphone.deviceId }
                };
                mic = "id - " + microphone.deviceId + " and label " + microphone.label;
            }
            this.logger.info("[WebrtcConfigAndUtils] getDefaultConstraintsForMedia for microphone " + mic);
        }

        if (userMedia.indexOf("video") >= 0) {
            constraints.video = true;
            const camera = this.deviceManagementService.getCurrentCamera();
            let cam = "default";

            if (camera?.deviceId && camera.deviceId !== "default") {
                constraints.video = {
                    deviceId: { exact: camera.deviceId }
                };
                cam = "id - " + camera.deviceId + " and label " + camera.label;
            }

            this.logger.info("[WebrtcConfigAndUtils] getDefaultConstraintsForMedia for camera " + cam);
        }

        if (!constraints.audio) { constraints.audio = false; }
        if (!constraints.video) { constraints.video = false; }

        return constraints;
    }

    private async setIceConfig() {
        try {
            if (this.webrtcConfig && this.ttl !== 0) {
                this.logger.info("[WebrtcConfigAndUtils] setIceConfig from stored variable");

                this.xmppService.connection.jingle.ice_config = this.webrtcConfig;
                return;
            }

            if (this.webrtcConfig) {
                //set the last known configuration, in case the API does not work
                this.xmppService.connection.jingle.ice_config = this.webrtcConfig;
            }

            const result = await this.getIceConfig();
            if (!this.webrtcConfig) {
                this.webrtcConfig = result;
                const enableDSCP = (this.settingsService.getSetting("enableDSCP") === "true");
                if (enableDSCP) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - DSCP is enabled");
                    this.webrtcConfig.enableDSCP = true;
                }

                const DTX = (this.settingsService.getSetting("dtx") === "true");
                if (DTX) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - DTX is enabled");
                    this.webrtcConfig.dtx = true;
                }

                const debugWeRTC = (this.settingsService.getSetting("debugWebRTC") === "true");
                if (debugWeRTC) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - debugWebRTC is enabled");
                    this.webrtcConfig.debugWeRTC = true;
                }

                //activate simulcast, this needs to be removed
                this.webrtcConfig.simulcast = true;

                const citrixConnected = (this.settingsService.getSetting("citrixConnected") === "true");
                if (citrixConnected) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - citrix is connected");
                    this.webrtcConfig["citrix"] = true;
                    //enable DTLS SRTP for citrix env
                    this.webrtcConfig["enableDtlsSrtp"] = true;
                    this.xmppService.connection.jingle.media_constraints = {
                        mandatory: {
                            OfferToReceiveAudio: true,
                            OfferToReceiveVideo: true
                        },
                        OfferToReceiveAudio: true,
                        OfferToReceiveVideo: true
                    };
                }

                const horizonConnected = (this.settingsService.getSetting("horizonConnected") === "true");
                if (horizonConnected) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - horizon is connected");
                    this.webrtcConfig["horizon"] = true;
                }

                const forceRedCodec = (this.settingsService.getSetting("forceRedCodec") === "true");
                if (forceRedCodec) {
                    this.logger.info("[WebrtcConfigAndUtils] setWebrtcConfiguration - forceRedCodec is enabled");
                    this.webrtcConfig.forceRedCodec = true;
                }
            }
            else {
                //keep the other settings that are already set perhaps from the SDK
                this.webrtcConfig.iceServers = result.iceServers;
                this.webrtcConfig.dc = result.dc;
            }

            this.manageIceServers();

            this.xmppService.connection.jingle.ice_config = this.webrtcConfig;
            this.logger.info("[WebrtcConfigAndUtils] setIceConfig from server success !");
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] setIceConfig from server ERROR : " + error);
            throw error;
        }
    }

    private manageIceServers() {
        //in case the application is CITRIX connected, we need to adapt the list of ICE servers
        if ((this.webrtcConfig["citrix"] || this.webrtcConfig["horizon"]) && this.webrtcConfig.iceServers?.length) {
            const citrixIceConfig = [{
                credential: '',
                urls: [],
                username: '' }
            ];
            this.webrtcConfig.iceServers.forEach((ice) => {
                citrixIceConfig[0].urls.push(ice.urls);
                citrixIceConfig[0].credential = ice.credential;
                citrixIceConfig[0].username = ice.username;
            });

            this.webrtcConfig.iceServers = citrixIceConfig;

            this.logger.info(`[WebrtcConfigAndUtils] setIceConfig for citrix env ${citrixIceConfig[0]?.urls?.toString()}`);
        }
    }

    private async getIceConfig() {
        const geoip = this.settingsService.getSetting("geoip");
        let geoipUrl = "";
        if (geoip) { geoipUrl = "&geoip=" + geoip; }

        //check for the number of servers to get
        let srvCount = 2;
        if (this.companyConfig?.negotiation?.turnSrvCount) srvCount = this.companyConfig?.negotiation?.turnSrvCount;

        //avoid wrong numbers
        if (srvCount < 1 || srvCount > 10) srvCount = 2;

        const portalURL = config.restServerUrl + "/api/rainbow/geolocation/v1.0/";
        const url = `${portalURL}settings/iceservers?nbServers=${srvCount.toString()}${geoipUrl}&jid=${this.xmppService.jid}`;

        try {
            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw (response);
            const result = (await response.json());

            this.ttl = result.ttl;
            if (this.ttl) {
                this.ttlTimeout = setTimeout(() => {
                    this.ttl = 0;
                    this.setWebrtcConfiguration();
                }, result.ttl * 1000);
            }

            const iceServers: Array<any> = result.data;
            const dc = [];
            iceServers.forEach(function (element) {
                this.logger.info(`[WebrtcConfigAndUtils] getIceConfig -- add new iceServer ${element?.urls} and dc ${element?.dc}`);
                if (element.dc && !dc.includes(element.dc)) { dc.push(element.dc); }
                delete element.dc;
                delete element.id;
            }, this);
            this.logger.info(`[WebrtcConfigAndUtils] getIceConfig success -- iceServers number ${iceServers.length.toString()} and dc number ${dc.length.toString()}`);
            return { iceServers: iceServers, dc: dc };
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] getIceConfig failure -- " + error.message);
            throw error;
        }
    }

    private async setCompanyWebrtcConfig() {
        try {
            if (this.companyConfig) {
                this.logger.info("[WebrtcConfigAndUtils] setCompanyWebrtcConfig from stored variable");

                this.xmppService.connection.jingle.company_config = this.companyConfig;
                this.setVideoQualityConfigurationForCompany();
                return;
            }
            this.logger.info("[WebrtcConfigAndUtils] setCompanyWebrtcConfig");
            this.companyConfig = await this.getCompanyWebrtcConfig();

            this.xmppService.connection.jingle.company_config = this.companyConfig;
            this.setVideoQualityConfigurationForCompany();
            this.logger.info("[WebrtcConfigAndUtils] setCompanyWebrtcConfig from server success !");
        }
        catch (error) {
            this.logger.error("[WebrtcConfigAndUtils] setCompanyWebrtcConfig from server ERROR : " + error);
            throw error;
        }
    }

    private setVideoQualityConfigurationForCompany() {
        if (this.companyConfig?.video) {
            this.videoQualityConfiguration.video.fixedVideoHeight = this.companyConfig.video.height ? this.companyConfig.video.height : this.videoQualityConfiguration.video.fixedVideoHeight;
            this.videoQualityConfiguration.video.fixedVideoWidth = this.companyConfig.video.width ? this.companyConfig.video.width : this.videoQualityConfiguration.video.fixedVideoWidth;
            this.videoQualityConfiguration.video.fixedFrameRate = this.companyConfig.video.frameRate ? this.companyConfig.video.frameRate : this.videoQualityConfiguration.video.fixedFrameRate;
        }

        if (this.companyConfig?.sharing) {
            this.videoQualityConfiguration.sharing.fixedVideoHeight = this.companyConfig.sharing.height ? this.companyConfig.sharing.height : this.videoQualityConfiguration.sharing.fixedVideoHeight;
            this.videoQualityConfiguration.sharing.fixedVideoWidth = this.companyConfig.sharing.width ? this.companyConfig.sharing.width : this.videoQualityConfiguration.sharing.fixedVideoWidth;
            this.videoQualityConfiguration.sharing.fixedFrameRate = this.companyConfig.sharing.frameRate ? this.companyConfig.sharing.frameRate : this.videoQualityConfiguration.sharing.fixedFrameRate;
        }

        if (this.companyConfig?.metrics) {
            this.webrtcSessionStatsHandler.setWebrtcMetricsValues(this.companyConfig.metrics);
        }
    }

    private async getCompanyWebrtcConfig() {
        try {
            const apiURL = config.restServerUrl + "/api/rainbow/enduser/v1.0/settings/webrtc";
            const response = await fetch(apiURL, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw (response);
            const result = (await response.json()).data;
            this.logger.info("[WebrtcConfigAndUtils] getCompanyWebrtcConfig success");
            return result;
        }
        catch (error) {
            const msg = (error?.data) ? error.data.errorDetails : "unknownError";
            if (error?.data && error.data.errorDetailsCode === 400100) {
                this.logger.info("[WebrtcConfigAndUtils] getCompanyWebrtcConfig -- " + msg);
                return {};
            }
            this.logger.error("[WebrtcConfigAndUtils] getCompanyWebrtcConfig failure -- " + msg);
            throw error;
        }
    }

    public getStatsForSession(session: any, interval: number = 5000, webinarId?: string) {
        if (session.webrtcStatsInterval) return session.webrtcStatsInterval;

        return this.webrtcSessionStatsHandler.getStatsForSession(session, interval, webinarId);
    }

    /**
     * For QA Tests Auto
     * Set/reset an interval for generating WebRTC statistics that will be used instead of the default 5s interval.
     * Set by default to 1000ms.
     * Usage: rainbow.services.WebrtcConfigAndUtils.setWebrtcStatsInterval(intervalMs);
     */
    public setWebrtcStatsInterval(interval: number = 1000) {
        // Check lower limit, stop processing if not in the [500...] range
        if (interval && interval < 500) {
            throw new Error(`[WebrtcConfigAndUtils] setWebrtcStatsInterval - Interval value must be greater than 500ms`);
        }
        localStorage.setItem("qaWebrtcStatsInterval", interval.toString());
    }

    public resetWebrtcStatsInterval() {
        localStorage.removeItem("qaWebrtcStatsInterval");
    }
}
